import { useTranslation } from 'react-i18next';
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { formatBytes } from '@/lib/drive/format';

export interface UploadTask {
  id: string;
  name: string;
  size: number;
  sent: number;
  status: 'waiting' | 'uploading' | 'done' | 'error' | 'canceled';
  error?: string;
}

interface UploadProgressPanelProps {
  tasks: UploadTask[];
  onCancel: (id: string) => void;
  onClear: () => void;
}

/**
 * The upload queue's floating panel: one row per file, live percentage and speed,
 * cancel per item. Sits in the corner of the drive surface so work stays visible
 * without blocking the listing.
 */
export function UploadProgressPanel({ tasks, onCancel, onClear }: UploadProgressPanelProps) {
  const { t } = useTranslation();
  if (tasks.length === 0) return null;
  const active = tasks.some((task) => task.status === 'uploading' || task.status === 'waiting');

  return (
    <div className="absolute bottom-4 end-4 z-30 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-border-color bg-card shadow-lg">
      <div className="flex flex-nowrap items-center justify-between gap-2 border-b border-border-color px-3 py-2">
        <p className="min-w-0 truncate whitespace-nowrap text-sm font-medium text-foreground">
          {active ? t('drive.uploadsActive', { count: tasks.filter((task) => task.status !== 'done').length }) : t('drive.uploadsDone')}
        </p>
        <button
          type="button"
          title={t('notifications.clearAll')}
          onClick={onClear}
          className="flex-shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
      <ul className="max-h-56 divide-y divide-border-color overflow-y-auto">
        {tasks.map((task) => {
          const percent = task.size > 0 ? Math.min(100, Math.round((task.sent / task.size) * 100)) : task.status === 'done' ? 100 : 0;
          return (
            <li key={task.id} className="flex items-center gap-2 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate whitespace-nowrap text-xs font-medium text-foreground" title={task.name}>
                  {task.name}
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
                    <div
                      className={clsx(
                        'h-full rounded-full transition-[width] duration-200',
                        task.status === 'error' ? 'bg-destructive' : task.status === 'canceled' ? 'bg-muted-foreground' : 'bg-accent',
                      )}
                      style={{ width: `${task.status === 'done' ? 100 : percent}%` }}
                    />
                  </div>
                  <span className="flex-shrink-0 whitespace-nowrap text-[10px] text-muted-foreground">
                    {task.status === 'done'
                      ? t('drive.uploadDone')
                      : task.status === 'error'
                        ? t('drive.uploadError')
                        : task.status === 'canceled'
                          ? t('drive.uploadCanceled')
                          : `${percent}% · ${formatBytes(task.sent)} / ${formatBytes(task.size)}`}
                  </span>
                </div>
              </div>
              {task.status === 'uploading' && (
                <button
                  type="button"
                  title={t('drive.uploadCancel')}
                  onClick={() => onCancel(task.id)}
                  className="flex-shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              )}
              {task.status === 'error' && (
                <ArrowPathIcon className="h-3.5 w-3.5 flex-shrink-0 text-destructive" />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
